<template>
    <modal
        id="create-partner-modal"
        title="Create partner card"
        :visible="show"
        scrollable
        ok-title="Proceed"
        size="lg"
        :ok-disabled="!partnerState"
        @ok="createPartner"
        @hide="clearInput"
        class="lx-global-modal"
    >
        <b-container fluid>
            <b-row class="my-1">
                <b-col sm="3">
                    <label for="input-title">Card title:</label>
                </b-col>
                <b-col sm="9">
                    <b-form-input
                        id="input-title"
                        v-model="title"
                        :state="partnerTitleState"
                        placeholder="Enter card title"
                        aria-describedby="input-1-live-feedback"
                    />
                </b-col>
                <b-col sm="3">
                    <label for="input-subtitle">Card subtitle:</label>
                </b-col>
                <b-col sm="9">
                    <b-form-input
                        id="input-subtitle"
                        v-model="subtitle"
                        :state="partnerSubtitleState"
                        placeholder="Enter subtitle text"
                        aria-describedby="input-live-help input-live-feedback"
                    />
                </b-col>
                <b-col sm="3">
                    <label for="input-link">Website link:</label>
                </b-col>
                <b-col sm="9">
                    <b-form-input
                        id="input-link"
                        v-model="websiteLink"
                        type="url"
                        placeholder="Enter partner website link"
                    />
                </b-col>
                <b-col sm="3">
                    <label for="icon-default">Card logo:</label>
                </b-col>
                <b-col sm="9">
                    <b-form-file accept="image/*" v-model="attachedLogo" id="icon-default"> </b-form-file>
                </b-col>
                <b-col sm="3">
                    <label for="select-category">Partner category:</label>
                </b-col>
                <b-col sm="9">
                    <b-form-select
                        id="select-category"
                        v-model="category"
                        :options="options"
                        :state="partnerCategorySelectState"
                        aria-describedby="input-live-help input-live-feedback"
                    />
                </b-col>
                <b-col sm="12">
                    <TextEditor v-model="modalText" ref="editor" :options="editorModules" />
                </b-col>
            </b-row>
        </b-container>
    </modal>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Modal from '../../../generic/modal/modal.vue';
    import API from '../../../.././api';
    import { defineAsyncComponent } from 'vue';
    const TextEditor = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-text-editor" */
            '../../../generic/editor/quill-editor.vue'
        )
    );

    export default {
        name: 'CreatePartnerModal',
        components: {
            Modal,
            TextEditor,
        },
        props: {
            show: {
                type: Boolean,
                default: false,
            },
            options: {
                type: Array,
                default: () => [],
            },
        },
        computed: {
            ...mapGetters('partner', ['partnerIds', 'partnerTitleById']),
            partnerTitleState() {
                return this.title.length > 0 ? true : false;
            },
            partnerSubtitleState() {
                return this.subtitle.length > 0 ? true : false;
            },
            partnerCategorySelectState() {
                if (this.category != null && this.category != undefined) {
                    return true;
                }
                return false;
            },
            fileUploadForbidden() {
                if (this.attachedLogo == null || this.attachedLogo == undefined) {
                    return true;
                }
                return false;
            },
            partnerState() {
                if (
                    this.partnerTitleState &&
                    this.partnerSubtitleState &&
                    this.partnerCategorySelectState &&
                    this.modalText.length > 0
                ) {
                    return true;
                }
                return false;
            },
        },
        data() {
            return {
                title: '',
                subtitle: '',
                category: null,
                attachedLogo: null,
                websiteLink: '',
                modalText: '',
                editorModules: {
                    toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        ['blockquote', 'code-block'],
                        [{ header: 1 }, { header: 2 }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        [{ script: 'sub' }, { script: 'super' }],
                        [{ indent: '-1' }, { indent: '+1' }],
                        [{ direction: 'rtl' }],
                        [{ size: ['small', false, 'large', 'huge'] }],
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        [{ font: [] }],
                        [{ color: [] }, { background: [] }],
                        [{ align: [] }],
                        ['clean'],
                        ['link', 'image'],
                    ],
                },
            };
        },
        methods: {
            clearInput() {
                this.title = '';
                this.subtitle = '';
                this.websiteLink = '';
                this.category = null;
                this.attachedLogo = null;
                this.modalText = '';
            },
            removeErrorResponseChars(text) {
                var detailDesc = text.replace(/[&]nbsp[;]/gi, ' ');
                detailDesc = detailDesc.replace(/[<]br[^>]*[>]/gi, '');
                return detailDesc;
            },
            isValidUrl(string) {
                try {
                    new URL(string);
                } catch (_) {
                    return false;
                }
                return true;
            },
            async createPartner() {
                let formData = new FormData();
                if (this.attachedLogo != null) {
                    formData.append('logo', this.attachedLogo);
                }
                formData.append('title', this.title);
                formData.append('subtitle', this.subtitle);
                if (this.isValidUrl(this.websiteLink)) {
                    formData.append('homepage', this.websiteLink);
                }
                formData.append('category', this.category);
                formData.append('modal_text', this.modalText);

                try {
                    this.$emit('startCreating');
                    await API.post('/app/partner-cards/info/', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    });
                    const message = 'Partner has been created';
                    this.successToast({ text: message });
                } catch (error) {
                    const response = error.response;
                    const detail =
                        response && response.data && response.data.detail
                            ? ' ' + this.removeErrorResponseChars(response.data.detail)
                            : '';
                    this.errorToast({ text: `Could not create partner card. ${detail}` });
                    return;
                } finally {
                    this.$emit('endCreating');
                    this.clearInput();
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    :deep(.container-fluid) {
        padding-left: 0px !important;
    }

    :deep(label) {
        margin-top: 0.4rem;
    }

    :deep(.col-sm-9) {
        padding-bottom: 16px;
    }

    :deep(.btn-primary.disabled),
    :deep(.btn-primary:disabled) {
        cursor: not-allowed;
    }
</style>
